import React, { useCallback } from 'react';
import { withStyles } from '@mui/styles';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import * as Validations from '../helper/Validations';
import { useTheme } from '@mui/material/styles';

const styles = theme => ({
  root: {
    width: '100%',
  },
});

const BirthDatePicker = React.memo(function BirthDatePicker({ classes, value, onChange, onInputChange, preventError = false }) {
  const theme = useTheme();
  
  const handleChange = useCallback((newValue) => {
    if (newValue && moment.isMoment(newValue)) {
      // Convert to ISO format for output without any timezone conversion
      // Extract year, month, day and construct ISO string manually
      const year = newValue.year();
      const month = String(newValue.month() + 1).padStart(2, '0'); // month() returns 0-11
      const day = String(newValue.date()).padStart(2, '0');
      const isoDate = `${year}-${month}-${day}T00:00:00.000Z`;
      onChange(isoDate);
      if (onInputChange) {
        onInputChange(isoDate);
      }
    } else {
      onChange('');
      if (onInputChange) {
        onInputChange('');
      }
    }
  }, [onChange, onInputChange]);

  // Convert ISO date to moment for display, or parse DD/MM/YYYY if legacy format
  const getMomentValue = () => {
    const isSentinel =
      value === '1900-01-01T00:00:00Z' ||
      value === '1900-01-01T00:00:00.000Z' ||
      value === '0001-01-01T00:00:00Z' ||
      value === '0001-01-01T00:00:00.000Z';
    if (!value || isSentinel) {
      return null;
    }
    
    // Check if it's already an ISO date
    if (moment(value, moment.ISO_8601, true).isValid()) {
      return moment(value);
    }
    
    // Check if it's DD/MM/YYYY format (legacy support)
    if (moment(value, 'DD/MM/YYYY', true).isValid()) {
      return moment(value, 'DD/MM/YYYY');
    }
    
    return null;
  };

  // Convert ISO date to DD/MM/YYYY for validation
  const getDisplayDateForValidation = () => {
    const isSentinel =
      value === '1900-01-01T00:00:00Z' ||
      value === '1900-01-01T00:00:00.000Z' ||
      value === '0001-01-01T00:00:00Z' ||
      value === '0001-01-01T00:00:00.000Z';
    if (!value || isSentinel) {
      return '';
    }
    
    // If it's already DD/MM/YYYY format, return as is
    if (moment(value, 'DD/MM/YYYY', true).isValid()) {
      return value;
    }
    
    // If it's ISO format, convert to DD/MM/YYYY for validation
    if (moment(value, moment.ISO_8601, true).isValid()) {
      return moment(value).format('DD/MM/YYYY');
    }
    
    return '';
  };

  const momentValue = getMomentValue();
  const displayDateForValidation = getDisplayDateForValidation();

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <MobileDatePicker
        className={classes.root}
        value={momentValue}
        onChange={handleChange}
        format="DD/MM/YYYY"
        minDate={moment().subtract(120, 'year')}
        maxDate={moment().subtract(18, 'year')}
        slotProps={{
          textField: {
            variant: 'standard',
            placeholder: 'DD/MM/YYYY',
            style: {
              width: '220px',
            },
            error: !preventError && displayDateForValidation && !Validations.validateBirthYear(displayDateForValidation),
            helperText:
              !preventError && displayDateForValidation && !Validations.validateBirthYear(displayDateForValidation)
                ? Validations.getInvalidDateReasonText(displayDateForValidation)
                : '',
            sx: {
              '& .MuiInputBase-root:before': {
                borderBottomColor: '#ccc', // default border
              },
              '& .MuiInputBase-root:after': {
                borderBottomColor: theme.palette.primary.main, // focus border
              },
              '& .MuiInputBase-input': {
                '&:focus': {
                  backgroundColor: '#fff3f3', // light red on focus
                },
              },
            },
          },
          day: {
            sx: {
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: 'white',
                '&:hover': {
                  backgroundColor: theme.palette.primary.main,
                },
              },
              '&:hover': {
                backgroundColor: `#e6eaef`,
                color: `white`,
              },
            },
          },
          actionBar: {
            sx: {
              backgroundColor: '#fff',
              '& .MuiButton-root': {
                color: theme.palette.primary.main,
                fontWeight: 400,
                fontSize: '0.9rem',
                letterSpacing: 1,
                textTransform: 'uppercase',
                minWidth: 54,
                margin: '0 8px',
                '&:hover': {
                  backgroundColor: `${theme.palette.primary.main}99`,
                  color: theme.palette.primary.contrastText,
                },
              },
            },
          },
        }}
      />
    </LocalizationProvider>
  );
});

export default withStyles(styles)(BirthDatePicker);
